import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import PaymentIcon from "@mui/icons-material/Payment";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelIcon from "@mui/icons-material/Cancel";
import { getMyPayments } from "../../services/UserApi/OrderApi";

const STATUS_CONFIG = {
  completed: { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: { ar: "مكتمل",       en: "Completed" }, Icon: CheckCircleIcon },
  pending:   { color: "#d97706", bg: "#fffbeb", border: "#fde68a", label: { ar: "قيد الانتظار", en: "Pending"   }, Icon: HourglassEmptyIcon },
  failed:    { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: { ar: "فشل",          en: "Failed"    }, Icon: CancelIcon },
};

const METHOD_LABELS = {
  easykash_card:   { ar: "بطاقة EasyKash",  en: "EasyKash Card"   },
  easykash_wallet: { ar: "محفظة EasyKash",  en: "EasyKash Wallet" },
  paymob:          { ar: "Paymob",           en: "Paymob"          },
  cash:            { ar: "نقدي",             en: "Cash"            },
};

export default function MyPaymentsTab({ isAr }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  useEffect(() => {
    let mounted = true;
    getMyPayments()
      .then((res) => {
        const data = res?.data || [];
        if (mounted) setPayments(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (mounted) setError(err?.response?.data?.message || err?.message || (isAr ? "فشل تحميل المدفوعات" : "Failed to load payments"));
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const getStatus = (s) =>
    STATUS_CONFIG[String(s || "").toLowerCase()] || {
      color: "#64748b", bg: "#f8fafc", border: "#e2e8f0",
      label: { ar: s || "—", en: s || "—" }, Icon: HourglassEmptyIcon,
    };

  const getMethod = (m) => {
    const entry = METHOD_LABELS[m];
    if (!entry) return m || "—";
    return isAr ? entry.ar : entry.en;
  };

  const totalCompleted = payments.filter((p) => p.status === "completed").reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const totalPending   = payments.filter((p) => p.status === "pending").reduce((s, p) => s + parseFloat(p.amount || 0), 0);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8, flexDirection: "column", gap: 2 }}>
        <CircularProgress size={48} sx={{ color: "#d5aa2f" }} />
        <Typography color="text.secondary" fontWeight={600}>
          {isAr ? "جاري تحميل مدفوعاتك..." : "Loading your payments..."}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ borderRadius: 3, mt: 2 }}>{error}</Alert>;
  }

  if (payments.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 4, md: 8 }, borderRadius: 4, textAlign: "center", border: "1px solid", borderColor: "grey.100", bgcolor: "#fff", mt: 2 }}>
        <AccountBalanceWalletIcon sx={{ fontSize: 64, color: "grey.200", mb: 2 }} />
        <Typography variant="h6" fontWeight={800} mb={1}>
          {isAr ? "لا توجد مدفوعات بعد" : "No payments yet"}
        </Typography>
        <Typography color="text.secondary">
          {isAr ? "ستظهر هنا مدفوعاتك بعد إتمام عملية الدفع" : "Your payments will appear here after checkout"}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* Summary Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[
          { label: isAr ? "إجمالي المدفوعات" : "Total Payments", value: payments.length,                                  color: "#d5aa2f", bg: "#fffbeb" },
          { label: isAr ? "مكتملة"           : "Completed",      value: payments.filter(p => p.status === "completed").length, color: "#16a34a", bg: "#f0fdf4" },
          { label: isAr ? "قيد الانتظار"     : "Pending",        value: payments.filter(p => p.status === "pending").length,   color: "#d97706", bg: "#fffbeb" },
          { label: isAr ? "المبلغ المكتمل"   : "Completed Amt",  value: `${totalCompleted.toFixed(2)} ${isAr ? "ج.م" : "EGP"}`, color: "#16a34a", bg: "#f0fdf4" },
          { label: isAr ? "المبلغ المعلق"    : "Pending Amt",    value: `${totalPending.toFixed(2)} ${isAr ? "ج.م" : "EGP"}`,   color: "#d97706", bg: "#fffbeb" },
        ].map((s) => (
          <Paper key={s.label} elevation={0} sx={{ flex: "1 1 140px", p: 2, borderRadius: 3, border: "1px solid", borderColor: "grey.100", bgcolor: s.bg, textAlign: "center" }}>
            <Typography variant="h6" fontWeight={900} sx={{ color: s.color, mb: 0.3 }}>{s.value}</Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", fontSize: "0.65rem", letterSpacing: 0.4 }}>
              {s.label}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Payments List */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {payments.map((p, index) => {
          const st = getStatus(p.status);
          const StatusIcon = st.Icon;

          return (
            <Paper
              key={p.id}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "grey.100",
                overflow: "hidden",
                bgcolor: "#fff",
                animation: `fadeInUp 0.4s ease-out ${index * 0.05}s both`,
                "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.07)" },
                "@keyframes fadeInUp": {
                  from: { opacity: 0, transform: "translateY(12px)" },
                  to:   { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              {/* Header */}
              <Box sx={{
                px: 2.5, py: 1.5,
                background: "linear-gradient(135deg, #1c1c1c 0%, #2d2d2d 100%)",
                display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <PaymentIcon sx={{ color: "#d5aa2f", fontSize: 18 }} />
                  <Typography variant="subtitle2" fontWeight={800} color="#fff">
                    #{p.merchant_order_id}
                  </Typography>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.3, borderRadius: 10, bgcolor: st.bg, border: `1px solid ${st.border}` }}>
                    <StatusIcon sx={{ fontSize: 13, color: st.color }} />
                    <Typography variant="caption" fontWeight={800} sx={{ color: st.color }}>
                      {isAr ? st.label.ar : st.label.en}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={900}
                  sx={{ background: "linear-gradient(135deg, #d5aa2f, #e6c76a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                >
                  {parseFloat(p.amount).toFixed(2)} {isAr ? "ج.م" : "EGP"}
                </Typography>
              </Box>

              {/* Details */}
              <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                {[
                  { label: isAr ? "رقم الطلب"      : "Order #",        value: p.order?.order_number || `#${p.order_id}` },
                  { label: isAr ? "طريقة الدفع"    : "Method",         value: getMethod(p.payment_method), isChip: true },
                  { label: isAr ? "حالة الطلب"     : "Order Status",   value: p.order?.status || "—" },
                  { label: isAr ? "حالة الدفع"     : "Payment Status", value: p.order?.payment_status || "—" },
                  { label: isAr ? "معرف المعاملة"  : "Transaction ID", value: p.transaction_id || "—", mono: true },
                  { label: isAr ? "التاريخ"         : "Date",           value: new Date(p.created_at).toLocaleDateString(isAr ? "ar-EG" : "en-GB") },
                ].map((cell) => (
                  <Box
                    key={cell.label}
                    sx={{
                      flex: "1 1 160px", px: 2.5, py: 1.5,
                      borderRight: "1px solid", borderBottom: "1px solid", borderColor: "grey.100",
                      "&:last-child": { borderRight: "none" },
                    }}
                  >
                    <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ display: "block", mb: 0.3, textTransform: "uppercase", fontSize: "0.65rem", letterSpacing: 0.4 }}>
                      {cell.label}
                    </Typography>
                    {cell.isChip ? (
                      <Chip label={cell.value} size="small" sx={{ bgcolor: "rgba(213,170,47,0.12)", color: "#b08c26", fontWeight: 800, height: 22, fontSize: "0.72rem" }} />
                    ) : (
                      <Typography variant="body2" fontWeight={700} sx={{ color: cell.mono ? "#64748b" : "text.primary", fontSize: cell.mono ? "0.72rem" : "0.875rem", fontFamily: cell.mono ? "monospace" : "inherit", wordBreak: "break-all" }}>
                        {cell.value}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
